import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const parseExcelSerialDate = (serial) => {
  const baseDate = new Date(1899, 11, 30)
  const jsDate = new Date(baseDate.getTime() + serial * 86400000)
  return jsDate.toISOString().slice(0, 10) // format: YYYY-MM-DD
}

export const parseDocJsonString = (docString) => {
  let parsedJson

  const cleanedDocString = docString.startsWith("b'")
    ? docString.slice(2, -1)
    : docString

  try {
    /** try direct parse */
    parsedJson = JSON.parse(cleanedDocString)
  } catch (error) {
    console.warn(
      'Direct JSON.parse failed, trying to unescape first...',
      error
    )

    const unescapedText = cleanedDocString.replace(/\\\\/g, '\\')

    const sanitizedText = unescapedText
      .replace(/\\n/g, '') // remove escaped newlines
      .replace(/\\'/g, "'") // fix invalid escaped apostrophes
      .replace(/\s{2,}/g, ' ') // normalize excessive spaces
      .trim()

    parsedJson = JSON.parse(sanitizedText)
  }

  return parsedJson
}

export const convertJsonDocToXlsx = (jsonDoc, fileName) => {
  const wb = XLSX.utils.book_new()

  /** do things in correct sheet order */
  jsonDoc.sheetOrder.forEach((sheetId) => {
    const sheet = jsonDoc.sheets[sheetId]
    const sheetData = []
    const cellData = sheet.cellData || {}

    /** convert cellData back to array format */
    Object.keys(cellData).forEach((rowKey) => {
      const rowIdx = parseInt(rowKey)
      sheetData[rowIdx] = sheetData[rowIdx] || []

      Object.keys(cellData[rowKey]).forEach((colKey) => {
        const colIdx = parseInt(colKey)
        const cell = cellData[rowKey][colKey]

        if (cell.t === 2 && typeof cell.v === 'number') {
          /** date cell: set value + format */
          sheetData[rowIdx][colIdx] = {
            v: cell.v,
            t: 'n', // treat as number
            z: 'yyyy-mm-dd', // apply date format
          }
        } else {
          /** regular cell */
          sheetData[rowIdx][colIdx] = cell.v
        }
      })
    })

    /** convert to xlsx sheet */
    const xlsxSheet = XLSX.utils.aoa_to_sheet(sheetData)
    XLSX.utils.book_append_sheet(wb, xlsxSheet, sheet.name || 'Sheet')
  })

  /** write file */
  XLSX.writeFile(wb, `${fileName}.xlsx`)
}

const convertToIWorksheetData = (sheetName, jsonData) => {
  const cellData = {}

  jsonData.forEach((row, rowIndex) => {
    row.forEach((cell, colIndex) => {
      if (cell !== undefined && cell !== null) {
        if (!cellData[rowIndex]) {
          cellData[rowIndex] = {}
        }

        /**
         * cell type guide
         * 2 = Number, 1 = String
         */
        cellData[rowIndex][colIndex] = {
          v: cell,
          t: typeof cell === 'number' ? 2 : 1
        }
      }
    })
  })

  return {
    name: sheetName,
    cellData,
    status: 1
  }
}

export const convertXlsxToJsonDoc = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'))
      return
    }

    const reader = new FileReader()
    reader.readAsArrayBuffer(file)

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result)
        const workbook = XLSX.read(data, { type: 'array' })

        const sheets = {}
        workbook.SheetNames.forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 })

          const worksheetData = convertToIWorksheetData(
            sheetName,
            jsonData
          )
          const sheetId = 'sheet-' + Date.now() // Generate unique ID
          worksheetData.id = sheetId

          sheets[sheetId] = worksheetData
        })

        const workbookData = {
          name: 'Imported Workbook',
          sheets
        }

        resolve(workbookData)
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = (e) => {
      reject(new Error('Failed to read file'))
    }
  })
}

export const convertJsonDocToPdf = (jsonDoc, fileName) => {
  const doc = new jsPDF()

  jsonDoc.sheetOrder.forEach((sheetId, sheetIdx) => {
    if (sheetIdx > 0) {
      doc.addPage()
    }

    const sheet = jsonDoc.sheets[sheetId]
    const cellData = sheet.cellData || {}
    const rowIndices = Object.keys(cellData).map(Number)
    const maxRowIndex = Math.max(...rowIndices, 0)
    const maxColIndex = Math.max(
      ...rowIndices.flatMap((row) => Object.keys(cellData[row] || {}).map(Number)),
      0
    )

    const aoa = []
    for (let i = 0; i <= maxRowIndex; i++) {
      aoa[i] = []
      for (let j = 0; j <= maxColIndex; j++) {
        const cell = cellData?.[i]?.[j]
        if (cell) {
          let value = cell.v
          if (cell.t === 2 && typeof value === 'number') {
            value = parseExcelSerialDate(value)
          }
          aoa[i][j] = value
        } else {
          aoa[i][j] = ''
        }
      }
    }

    const [headerRow = [], ...bodyRows] = aoa

    doc.setFontSize(12)
    doc.text(sheet.name || `Sheet ${sheetIdx + 1}`, 14, 15)

    autoTable(doc, {
      startY: 20,
      head: [headerRow],
      body: bodyRows,
      theme: 'grid',
      styles: { fontSize: 8 },
    })
  })

  doc.save(`${fileName}.pdf`)
}

export const appendParamsToUrl = (url, params) => {
  const hasQuery = url.includes('?')
  const separator = hasQuery ? '&' : '?'
  const paramString = new URLSearchParams(params).toString()
  return `${url}${separator}${paramString}`
}
